<template>
  <div class="relative w-full max-w-5xl mx-auto my-8 bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
    <div class="aspect-square relative overflow-hidden">
      <svg viewBox="0 0 1000 1000" class="w-full h-full font-inter">
        <g transform="translate(500, 500)">
          
          <!-- Ring boundaries -->
          <circle r="460" class="fill-white stroke-slate-200 stroke-[1]" />
          <circle r="360" class="fill-white stroke-slate-200 stroke-[1]" />
          <circle r="250" class="fill-white stroke-slate-200 stroke-[1]" />
          <circle r="130" class="fill-white stroke-slate-200 stroke-[1]" />

          <!-- Center Cross Gap -->
          <rect x="-500" y="-12" width="1000" height="24" fill="white" />
          <rect x="-12" y="-500" width="24" height="1000" fill="white" />

          <!-- Quadrant Borders -->
          <line x1="-460" y1="-12" x2="-12" y2="-12" class="stroke-slate-300 stroke-[1]" />
          <line x1="12" y1="-12" x2="460" y2="-12" class="stroke-slate-300 stroke-[1]" />
          <line x1="-460" y1="12" x2="-12" y2="12" class="stroke-slate-300 stroke-[1]" />
          <line x1="12" y1="12" x2="460" y2="12" class="stroke-slate-300 stroke-[1]" />
          
          <line x1="-12" y1="-460" x2="-12" y2="-12" class="stroke-slate-300 stroke-[1]" />
          <line x1="12" y1="-460" x2="12" y2="-12" class="stroke-slate-300 stroke-[1]" />
          <line x1="-12" y1="12" x2="-12" y2="460" class="stroke-slate-300 stroke-[1]" />
          <line x1="12" y1="12" x2="12" y2="460" class="stroke-slate-300 stroke-[1]" />

          <!-- Ring Labels (Left Side) -->
          <text x="-410" y="4" class="text-sm font-bold fill-slate-400" text-anchor="middle" dominant-baseline="middle">Hold</text>
          <text x="-305" y="4" class="text-sm font-bold fill-slate-400" text-anchor="middle" dominant-baseline="middle">Assess</text>
          <text x="-190" y="4" class="text-sm font-bold fill-slate-400" text-anchor="middle" dominant-baseline="middle">Trial</text>
          <text x="-70" y="4" class="text-sm font-bold fill-slate-400" text-anchor="middle" dominant-baseline="middle">Adopt</text>

          <!-- Ring Labels (Right Side) -->
          <text x="70" y="4" class="text-sm font-bold fill-slate-400" text-anchor="middle" dominant-baseline="middle">Adopt</text>
          <text x="190" y="4" class="text-sm font-bold fill-slate-400" text-anchor="middle" dominant-baseline="middle">Trial</text>
          <text x="305" y="4" class="text-sm font-bold fill-slate-400" text-anchor="middle" dominant-baseline="middle">Assess</text>
          <text x="410" y="4" class="text-sm font-bold fill-slate-400" text-anchor="middle" dominant-baseline="middle">Hold</text>

          <!-- Corner Labels -->
          <text x="-460" y="-460" class="text-2xl font-extrabold fill-slate-800" text-anchor="start">Techniques <tspan class="fill-emerald-500">></tspan></text>
          <text x="460" y="-460" class="text-2xl font-extrabold fill-slate-800" text-anchor="end">Tools <tspan class="fill-emerald-500">></tspan></text>
          <text x="-460" y="460" class="text-2xl font-extrabold fill-slate-800" text-anchor="start">Platforms <tspan class="fill-emerald-500">></tspan></text>
          <text x="460" y="440" class="text-2xl font-extrabold fill-slate-800" text-anchor="end">Languages &</text>
          <text x="460" y="465" class="text-2xl font-extrabold fill-slate-800" text-anchor="end">Frameworks <tspan class="fill-emerald-500">></tspan></text>

          <!-- Blips -->
          <g v-for="(blip, index) in plottedItems" :key="index"
             @mouseenter="hoveredBlip = blip"
             @mouseleave="hoveredBlip = null"
             class="cursor-pointer transition-transform hover:scale-[1.3] origin-center"
             :style="{ transformOrigin: `${blip.x}px ${blip.y}px` }">
            <a :href="blip.href" target="_blank">
              <!-- Outline styling -->
              <circle :cx="blip.x" :cy="blip.y" r="14" fill="white" :stroke="blip.color" stroke-width="2.5" class="shadow-sm" />
              <text :x="blip.x" :y="blip.y + 1" :fill="blip.color" class="text-[12px] font-bold select-none" text-anchor="middle" dominant-baseline="middle">{{ blip.id }}</text>
            </a>
          </g>
        </g>
      </svg>
      
      <!-- Tooltip -->
      <div v-if="hoveredBlip" 
           class="absolute z-50 pointer-events-none bg-slate-900 text-white p-3 rounded-lg shadow-xl max-w-xs transition-opacity border border-slate-700"
           :style="{ left: hoveredBlip.px, top: hoveredBlip.py, transform: 'translate(-50%, -120%)' }">
        <div class="font-bold text-sm mb-1 text-white">{{ hoveredBlip.title }}</div>
        <div class="text-[10px] uppercase font-bold tracking-wider mb-2" :style="{ color: hoveredBlip.color }">
          {{ hoveredBlip.quadrant }} • {{ hoveredBlip.status }}
        </div>
        <div class="text-xs text-slate-300 line-clamp-3 leading-relaxed">{{ hoveredBlip.content }}</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "RadarChart",
  props: {
    items: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      hoveredBlip: null
    };
  },
  computed: {
    plottedItems() {
      // Configuration for rings
      const rings = {
        'Adopt': { min: 30, max: 110 },
        'Trial': { min: 150, max: 230 },
        'Assess': { min: 270, max: 340 },
        'Hold': { min: 380, max: 440 },
        'Caution': { min: 380, max: 440 }
      };

      const quadrants = {
        'techniques': { min: -170, max: -100, color: '#0ea5e9' }, // sky-500
        'tools': { min: -80, max: -10, color: '#10b981' }, // emerald-500
        'platforms': { min: 100, max: 170, color: '#f59e0b' }, // amber-500
        'languages and frameworks': { min: 10, max: 80, color: '#e11d48' } // rose-600
      };

      const hashString = (str) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
          hash = ((hash << 5) - hash) + str.charCodeAt(i);
          hash |= 0;
        }
        return Math.abs(hash);
      };

      const seededRandom = (seed) => {
        const x = Math.sin(seed) * 10000;
        return x - Math.floor(x);
      };

      const plotted = [];
      const distance = (p1, p2) => Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));

      return this.items.map((item, index) => {
        const statusKey = item.status === 'Hold' ? 'Hold' : item.status;
        const rConf = rings[statusKey] || { min: 0, max: 0 };
        
        // Fix: Ensure quadrant string is lowercase to match our dictionary keys
        const quadKey = (item.quadrant || '').toLowerCase();
        const qConf = quadrants[quadKey] || { min: 0, max: 0, color: '#94a3b8' }; // slate-400 default
        
        let seed = hashString(item.title);
        let x, y, radius, angleRad;
        
        let attempts = 0;
        let overlap = true;
        
        while (overlap && attempts < 50) {
          radius = rConf.min + seededRandom(seed) * (rConf.max - rConf.min);
          const angleDeg = qConf.min + seededRandom(seed + 1) * (qConf.max - qConf.min);
          angleRad = angleDeg * (Math.PI / 180);
          
          x = radius * Math.cos(angleRad);
          y = radius * Math.sin(angleRad);
          
          overlap = plotted.some(p => distance(p, {x, y}) < 32);
          seed += 100;
          attempts++;
        }

        plotted.push({x, y});

        const px = ((x + 500) / 1000) * 100 + '%';
        const py = ((y + 500) / 1000) * 100 + '%';

        return {
          ...item,
          id: index + 1,
          x,
          y,
          px,
          py,
          color: qConf.color
        };
      });
    }
  }
};
</script>
